/**
 * trucks + drivers + shifts + timeclock_events (DB-04 + DEC-011 / §86).
 *
 * The vehicles-and-drivers domain. Drivers EXTEND users (NOT a duplicate
 * identity table) — one drivers row per (user_id, tenant_id) where the user
 * holds DRIVER or LEAD_DRIVER in tenant_users. Drivers carry the master spec
 * §37 compliance fields (CDL / TWIC / hazmat / DOT medical).
 *
 * Trucks are declared here per CONTEXT D-19 — DEC-011 / master spec §86 places
 * the trucks table in the vehicles-and-drivers domain alongside drivers.
 * Trucks carry the §37 vehicle compliance fields (DOT / MC / plate / state /
 * registration / inspection / tank capacity) plus DEC-011 yard columns.
 *
 * Shifts use the DERIVED open-shift predicate (no `is_open` boolean):
 *   open_shift := ended_at IS NULL
 * A partial index on `(tenant_id, driver_id) WHERE ended_at IS NULL` makes
 * the open-shift lookup index-only (RESEARCH §shifts.is_open + perf #13).
 *
 * timeclock_events implements DEC-001 + master spec §11's EVENT CHAIN MODEL:
 * append-only, immutable, with `previous_event_id` self-FK forming a
 * tamper-evident chain. The event_type allowlist is EXACTLY 4 values
 * verbatim from master spec §11 line 571: STARTED / PAUSED / RESUMED /
 * STOPPED. Shift start/end timestamps live on the `shifts` table; this
 * table is for per-job clock transitions only.
 *
 * EXPLICIT NON-FEATURE: there is NO `paused_minutes` accumulator column on
 * `shifts` or anywhere else. DEC-001 + master spec §11 ban this anti-pattern.
 * Total paused time is DERIVED at read time from the timeclock_events chain
 * (PAUSED + RESUMED pairs, master spec §11). Phase 12 billing computes hours
 * from this same chain (BILL-03 success criterion).
 *
 * Legacy bugs prevented (per docs/BUSINESS_RULES.md §1.9):
 *   - Bug A: PausedMinutes accumulator drift when STOPPED event sync'd but
 *     accumulator write didn't.
 *   - Bug B: Same accumulator showing wrong total when events arrive out of
 *     order.
 *
 * The append-only event chain has neither bug class — total paused time is
 * a function of the chain, not a stored field.
 *
 * RLS posture (CONTEXT D-15): every table here is tenant-scoped. Each gets a
 * pgPolicy with the standard
 *   `tenant_id = current_setting('app.current_tenant_id')::uuid`
 * predicate. 4 pgPolicy calls total: trucks, drivers, shifts, timeclock_events.
 *
 * Cross-plan FK wiring (Plan 02-04 finalized — DEC-011 / §86 yard columns):
 *   - drivers.home_yard_id        → yards.id (set null)
 *   - drivers.current_yard_id     → yards.id (set null)
 *   - shifts.started_at_yard_id   → yards.id (set null)
 *   - shifts.ended_at_yard_id     → yards.id (set null)
 *   - trucks.current_yard_id      → yards.id (set null)
 *   - trucks.home_yard_id         → yards.id (set null)
 *   All six FKs were deferred from Plan 02-02 to preserve Wave 1 parallelism;
 *   Plan 02-04 modified this file to add the FK constraints once Plan 02-03's
 *   yards.ts existed. Inline `.references()` approach (with
 *   `import { yards } from './yards.js'`) is the cleanest pattern. No
 *   circular import — yards.ts does NOT import drivers.ts.
 *
 * Sources:
 *   - master spec §10 (DRIVER SHIFT OPERATIONS — shifts columns)
 *   - master spec §11 (CLOCK AND BILLING — EVENT CHAIN MODEL — line 571 4-value
 *     event_type allowlist + immutable append-only contract)
 *   - master spec §37 (VEHICLE AND DRIVER COMPLIANCE FIELDS — drivers + trucks)
 *   - master spec §86 (YARDS, TRUCKS, AND TRAILERS — DEC-011)
 *   - DEC-001 (event-chain timeclock — no PausedMinutes accumulator)
 *   - DEC-011 (yards core platform feature — column adds + trucks declaration)
 *   - CONTEXT D-18 (DEC-001 confirmation)
 *   - CONTEXT D-19 (Phase 2 schema addendum recording DEC-011)
 *   - RESEARCH §shifts.is_open (derived predicate; partial index)
 *   - RESEARCH Performance Indexes #12-16 (shifts: 2; timeclock_events: 3)
 *
 * Locked contracts (future phases must NOT change without DEC-NNN):
 *   - timeclock_events.event_type allowlist is exactly 4 values (master spec §11
 *     line 571): STARTED / PAUSED / RESUMED / STOPPED.
 *   - timeclock_events is APPEND-ONLY. Phase 5+ application code MUST NOT
 *     UPDATE or DELETE rows. A defense-in-depth Phase 23 may add a Postgres
 *     trigger that REVOKEs UPDATE/DELETE for app_user.
 *   - shifts has NO `is_open` boolean and NO `paused_minutes` column.
 *   - drivers extends users via FK (one users row, multiple drivers rows
 *     possible across tenants for the same user).
 *
 * NEGATIVE NOTE — `multi_yard_dispatch` is NOT a column anywhere in this file
 * (DEC-011 sweep). Yard visibility for dispatchers is controlled exclusively
 * by `dispatcher_yard_assignments` (Plan 02-03's yards.ts).
 */

import {
  boolean,
  check,
  date,
  foreignKey,
  index,
  integer,
  numeric,
  pgPolicy,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
  varchar,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { appUserRole } from './enums.js';
import { tenants, users } from './users.js';
import { jobs } from './jobs.js';
import { yards } from './yards.js';
import { importBatches } from './imports.js';

/**
 * trucks — DEC-011 / master spec §86.
 *
 * Tenant-scoped (RLS via standard predicate). Hauler-only enforcement is
 * Phase 3 role-guard's job (no schema-level CHECK; documented in DEC-011
 * ledger). Holds master spec §37 vehicle compliance fields plus DEC-011
 * yard columns.
 *
 * Cross-plan FK forward references:
 *   - trailers.assigned_truck_id (Plan 02-03 trailers.ts) → trucks.id —
 *     Plan 02-04 relations.ts wires the FK constraint.
 *   - trucks.current_yard_id / home_yard_id → yards.id (Plan 02-03 yards.ts)
 *     — Plan 02-04 relations.ts wires the FK constraint.
 */
export const trucks = pgTable(
  'trucks',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    // Hauler-internal asset number, e.g. "Truck 14" or "T-014".
    unitNumber: text('unit_number').notNull(),
    // Quick 260616-flt / DEC-067 — free-text truck type for the Fleet UI (migration 0030).
    truckType: text('truck_type'),
    // Migration 0085 — QuickBooks Class path, e.g. `Calumet:Tractors:112`.
    //
    // STORED, not derived. It looks like `<yard>:<truck type>:<unit number>`, all
    // three of which live on this table, but the customer's real export disproves
    // any derivation: `Calumet:Tractors:79` on unit `079`, `Kill Truck:001` with no
    // yard segment at all, and `WATONGA:Tractors:126` on unit `129`.
    //
    // EXEMPT FROM DEC-147. This is an external key matched against QuickBooks' own
    // class list, not a hauler-entered identifier. Upper-casing it would produce a
    // DIFFERENT class in QuickBooks and file the ticket to the wrong cost centre.
    // Store and export verbatim; trim whitespace and nothing else.
    accountingClass: text('accounting_class'),
    // Master spec §37 vehicle compliance fields.
    dotNumber: varchar('dot_number', { length: 20 }),
    mcNumber: varchar('mc_number', { length: 20 }),
    licensePlate: varchar('license_plate', { length: 20 }),
    licenseState: varchar('license_state', { length: 2 }),
    registrationExpiry: date('registration_expiry'),
    lastInspectionDate: date('last_inspection_date'),
    tankCapacityBbl: numeric('tank_capacity_bbl', { precision: 10, scale: 2 }),
    // DEC-011 / master spec §86 yard columns — FK wired in Plan 02-04.
    // NULL current_yard_id = on a job or location unknown.
    currentYardId: uuid('current_yard_id').references(() => yards.id, { onDelete: 'set null' }),
    // NULL home_yard_id = no default home yard assigned.
    homeYardId: uuid('home_yard_id').references(() => yards.id, { onDelete: 'set null' }),
    // Quick 260616-fld (Fleet UI prep) — a truck's STANDING default driver +
    // trailer (NULL = no default). Migration 0029 owns the actual FK constraints.
    //   default_driver_id → drivers.id: inline .references is safe — drivers is
    //     declared later in THIS file and the thunk resolves it lazily (no import).
    //   default_trailer_id → trailers.trailer_id: SOFT uuid column (NO inline
    //     .references). drivers.ts must NOT import trailers.ts (that would create
    //     the circular import drivers.ts↔trailers.ts the schema deliberately
    //     avoids — see trailers.ts docstring). The FK is enforced by migration
    //     0029 and the Drizzle relation is wired in relations.ts.
    defaultDriverId: uuid('default_driver_id').references(() => drivers.id, {
      onDelete: 'set null',
    }),
    defaultTrailerId: uuid('default_trailer_id'),
    isActive: boolean('is_active').notNull().default(true),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
    // Task 20.5 provenance marker — set only by the import commit engine (20.6).
    // NULL means "not created by an import", which is every row today.
    importBatchId: uuid('import_batch_id'),
  },
  (t) => [
    // Task 20.5 — per-batch reconciliation and undo. Partial: almost no row
    // is import-created, so a full index would be mostly NULLs.
    index('trucks_import_batch_idx')
      .on(t.tenantId, t.importBatchId)
      .where(sql`import_batch_id IS NOT NULL`),
    // Task 20.6a / DEC-265 — composite so a row cannot claim ANOTHER tenant's
    // batch. Declared here rather than as a column .references() because the
    // reference spans two columns. One detail Drizzle cannot express: the
    // migration uses ON DELETE SET NULL ("import_batch_id"), the Postgres 15
    // column-scoped form, because a bare SET NULL would also null tenant_id
    // (NOT NULL) and make deleting a batch fail. 0107 is authoritative.
    foreignKey({
      columns: [t.tenantId, t.importBatchId],
      foreignColumns: [importBatches.tenantId, importBatches.id],
      name: 'trucks_import_batch_tenant_fk',
    }).onDelete('set null'),
    // UNIQUE (tenant_id, unit_number) — a hauler can't have two Truck 14s.
    uniqueIndex('trucks_tenant_unit_number_idx').on(t.tenantId, t.unitNumber),
    // Active-trucks list per tenant.
    index('trucks_tenant_active_idx').on(t.tenantId, t.isActive),
    // FK indexes for the deferred yard FKs (master spec §86 explicit indexes).
    index('trucks_current_yard_idx').on(t.currentYardId),
    index('trucks_home_yard_idx').on(t.homeYardId),
    // Quick 260616-fld (Fleet UI prep) — FK indexes for the default assignments.
    index('trucks_default_driver_idx').on(t.defaultDriverId),
    index('trucks_default_trailer_idx').on(t.defaultTrailerId),
    pgPolicy('trucks_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type Truck = typeof trucks.$inferSelect;
export type NewTruck = typeof trucks.$inferInsert;
export const truckSelectSchema = createSelectSchema(trucks);
export const truckInsertSchema = createInsertSchema(trucks);

/**
 * drivers — extends users via FK.
 *
 * One row per (user_id, tenant_id) where the user holds DRIVER or LEAD_DRIVER
 * in tenant_users. The drivers row carries the regulatory compliance metadata
 * (CDL / TWIC / hazmat / DOT medical) per master spec §37; tenant_users
 * carries the role assignment.
 *
 * RLS-enforced. UNIQUE (tenant_id, user_id) — one drivers row per user per
 * tenant.
 *
 * DEFERRED FKs: home_yard_id + current_yard_id → yards.id (DEC-011 / §86) —
 * see module docstring.
 */
export const drivers = pgTable(
  'drivers',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    // Master spec §37 — driver compliance fields. Stored plaintext: these are
    // regulatory identifiers (driver's license / transportation worker ID),
    // not credentials. T-02-02-08 acceptance.
    cdlNumber: text('cdl_number'),
    cdlState: varchar('cdl_state', { length: 2 }),
    cdlExpiresAt: date('cdl_expires_at'),
    twicNumber: text('twic_number'),
    twicExpiresAt: date('twic_expires_at'),
    hazmatEndorsement: boolean('hazmat_endorsement').notNull().default(false),
    hazmatExpiresAt: date('hazmat_expires_at'),
    dotMedicalCardExpiresAt: date('dot_medical_card_expires_at'),
    // Tenant-internal HR id (optional).
    employeeCode: text('employee_code'),
    // DEC-011 / master spec §86 yard columns — FK wired in Plan 02-04.
    homeYardId: uuid('home_yard_id').references(() => yards.id, { onDelete: 'set null' }),
    currentYardId: uuid('current_yard_id').references(() => yards.id, { onDelete: 'set null' }),
    // Wave 3 Part 2 (Quick 260615-uf9) — assigned shift for the dispatch board's
    // DAY/NIGHT grouping. text + CHECK ('DAY'|'NIGHT'), default DAY (migration 0028).
    shift: text('shift').notNull().default('DAY'),
    // ClickUp 86bbrwg66 / master spec §10 (migration 0082) — the driver's PERSISTED
    // equipment default, used to PRE-FILL the mandatory shift-start confirm prompt.
    //
    // WRITTEN ONLY BY EXPLICIT DRIVER CONFIRMATION — the shift-start confirm
    // endpoint and the mid-shift change endpoint. NOTHING on the job-completion
    // path may write these. Legacy OutSystems `UpdateDriverTruckTrailer` copied the
    // request's truck/trailer here on EVERY completion (docs/BUSINESS_RULES.md:81),
    // so one shift in a rental truck permanently switched a driver's default. Spec
    // §10's "NEVER auto-updates on job completion" exists to prevent exactly that.
    //
    // Independent of trucks.default_driver_id / default_trailer_id (Quick
    // 260616-fld) — that is the dispatcher-facing "usual crew for this truck" view.
    // No sync either way; they may legitimately disagree.
    //
    // BOTH are SOFT uuid columns (no inline .references) — FKs owned by migration
    // 0081, relations wired in relations.ts:
    //
    //   default_trailer_id → trailers.trailer_id: drivers.ts must NOT import
    //     trailers.ts (the circular import the schema deliberately avoids — see the
    //     trailers.ts docstring). Same reason as trucks.default_trailer_id.
    //
    //   default_truck_id → trucks.id: trucks is declared ABOVE in this same file, so
    //     a lazy thunk would normally resolve — but trucks.default_driver_id already
    //     references drivers.id, and adding the opposite inline reference closes a
    //     MUTUAL cycle that TypeScript cannot infer through (TS7022/TS7024: "'trucks'
    //     implicitly has type 'any' because it is referenced directly or indirectly
    //     in its own initializer"). The two columns point opposite ways between the
    //     same pair of tables, which is exactly the cycle. Dropping the inline
    //     .references on THIS side breaks it; the dispatcher-facing
    //     trucks.default_driver_id keeps its inline FK unchanged.
    defaultTruckId: uuid('default_truck_id'),
    defaultTrailerId: uuid('default_trailer_id'),
    isActive: boolean('is_active').notNull().default(true),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
    // Task 20.5 provenance marker — set only by the import commit engine (20.6).
    // NULL means "not created by an import", which is every row today.
    importBatchId: uuid('import_batch_id'),
  },
  (t) => [
    // Task 20.5 — per-batch reconciliation and undo. Partial: almost no row
    // is import-created, so a full index would be mostly NULLs.
    index('drivers_import_batch_idx')
      .on(t.tenantId, t.importBatchId)
      .where(sql`import_batch_id IS NOT NULL`),
    // Task 20.6a / DEC-265 — composite so a row cannot claim ANOTHER tenant's
    // batch. Declared here rather than as a column .references() because the
    // reference spans two columns. One detail Drizzle cannot express: the
    // migration uses ON DELETE SET NULL ("import_batch_id"), the Postgres 15
    // column-scoped form, because a bare SET NULL would also null tenant_id
    // (NOT NULL) and make deleting a batch fail. 0107 is authoritative.
    foreignKey({
      columns: [t.tenantId, t.importBatchId],
      foreignColumns: [importBatches.tenantId, importBatches.id],
      name: 'drivers_import_batch_tenant_fk',
    }).onDelete('set null'),
    // One drivers row per user per tenant.
    uniqueIndex('drivers_tenant_user_idx').on(t.tenantId, t.userId),
    // Active drivers list per tenant.
    index('drivers_tenant_active_idx').on(t.tenantId, t.isActive),
    // ClickUp 86bbrwg66 (migration 0082) — FK indexes for the persisted defaults.
    index('drivers_default_truck_idx').on(t.defaultTruckId),
    index('drivers_default_trailer_idx').on(t.defaultTrailerId),
    pgPolicy('drivers_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
    // Wave 3 Part 2 — DAY/NIGHT shift allowlist (migration 0028).
    check('drivers_shift_check', sql`shift IN ('DAY', 'NIGHT')`),
  ],
);

export type Driver = typeof drivers.$inferSelect;
export type NewDriver = typeof drivers.$inferInsert;
export const driverSelectSchema = createSelectSchema(drivers);
export const driverInsertSchema = createInsertSchema(drivers);

/**
 * shifts — open-shift = `ended_at IS NULL` (no `is_open` boolean).
 *
 * RLS-enforced. The driver_id column references users.id (NOT drivers.id) per
 * master spec §10's identity model — shifts attach to a person, not to a
 * compliance record. (drivers exists for compliance metadata; the actual
 * "user who clocked in" is in users.)
 *
 * Two indexes (RESEARCH #12, #13):
 *   - (tenant_id, driver_id, started_at DESC) — shift history per driver
 *   - PARTIAL (tenant_id, driver_id) WHERE ended_at IS NULL — open-shift lookup
 *     ("does this driver have an open shift right now?")
 *
 * NO `is_open` boolean. NO `paused_minutes` accumulator. DEC-001 + master spec
 * §11 ban these anti-patterns.
 *
 * DEFERRED FKs: started_at_yard_id + ended_at_yard_id → yards.id (DEC-011 / §86)
 * — see module docstring. started_truck_id and ended_truck_id are also soft
 * columns; Plan 02-04 relations.ts wires those to trucks.id.
 */
export const shifts = pgTable(
  'shifts',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    driverId: uuid('driver_id')
      .notNull()
      .references(() => users.id, { onDelete: 'restrict' }),
    startedAt: timestamp('started_at', { withTimezone: true }).notNull(),
    // FK to trucks.id added in Plan 02-04 relations.ts (cross-domain wiring).
    //
    // ClickUp 86bbrwg66 — started_truck_id is the IMMUTABLE shift-start record. It
    // belongs to the started_at / started_at_yard_id / started_gps_* group and must
    // NEVER be mutated mid-shift; the live value is active_truck_id below. A single
    // mutable column cannot serve both roles — see the 0081 migration header.
    startedTruckId: uuid('started_truck_id'),
    // ClickUp 86bbrwg66 (migration 0082) — trailer sibling for the truck bookend.
    // §86: "a driver picks up a truck and a trailer independently each shift".
    startedTrailerId: uuid('started_trailer_id'),
    // ClickUp 86bbrwg66 (migration 0082) — CURRENT equipment, mutable mid-shift.
    // Spec §10's `active_truck_id` ("what gets stamped on all jobs this shift; can
    // change mid-shift from app menu"). This is what the dispatch board reads.
    //
    // A current-state POINTER, not a DEC-001-banned accumulator: there is no running
    // total, so nothing can drift. Each change writes this column and its
    // TRUCK_CHANGED audit_events row in ONE transaction.
    activeTruckId: uuid('active_truck_id'),
    activeTrailerId: uuid('active_trailer_id'),
    // NULL = open shift; the shifts_open_lookup_idx partial index covers this.
    endedAt: timestamp('ended_at', { withTimezone: true }),
    // Migration 0159 (OPEN-110) — why it ended: DRIVER_END_DAY | NEW_SHIFT_START |
    // IDLE_AUTO_CLOSE | MAX_DURATION. NULL for shifts ended before 0159 (unknown).
    endedReason: text('ended_reason'),
    endedTruckId: uuid('ended_truck_id'),
    endedTrailerId: uuid('ended_trailer_id'),
    // DEC-011 / master spec §86 yard columns — FK wired in Plan 02-04.
    startedAtYardId: uuid('started_at_yard_id').references(() => yards.id, {
      onDelete: 'set null',
    }),
    endedAtYardId: uuid('ended_at_yard_id').references(() => yards.id, { onDelete: 'set null' }),
    startedGpsLat: numeric('started_gps_lat', { precision: 10, scale: 7 }),
    startedGpsLng: numeric('started_gps_lng', { precision: 10, scale: 7 }),
    endedGpsLat: numeric('ended_gps_lat', { precision: 10, scale: 7 }),
    endedGpsLng: numeric('ended_gps_lng', { precision: 10, scale: 7 }),
    // Migration 0190 (offline shift start) — how the shift began: ONLINE_START (POST /start),
    // OFFLINE_START (a queued shift_start), AUTO_SYNC (lazily opened by a queued job event).
    openedVia: text('opened_via').notNull().default('ONLINE_START'),
    // Migration 0190 — the DEVICE time of the equipment decision now in active_*; a queued
    // change applies only when newer (last write wins by when the driver chose).
    equipmentSetAt: timestamp('equipment_set_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    // RESEARCH perf index #12 — shift history per driver (newest first).
    index('shifts_tenant_driver_started_idx').on(t.tenantId, t.driverId, t.startedAt.desc()),
    // Migration 0190 — one open shift per driver. Every INSERT takes the per-driver lock
    // first (services/open-shift.ts), so this never turns a race into a 500.
    uniqueIndex('shifts_one_open_per_driver_idx')
      .on(t.tenantId, t.driverId)
      .where(sql`ended_at IS NULL`),
    // RESEARCH perf index #13 — PARTIAL open-shift lookup. The WHERE clause
    // narrows the index to only open shifts (typically a small subset of all
    // shifts), making "does driver X have an open shift?" an index-only scan.
    index('shifts_open_lookup_idx')
      .on(t.tenantId, t.driverId, t.startedAt)
      .where(sql`ended_at IS NULL`),
    // ClickUp 86bbrwg66 (migration 0082) — equipment joins hung off the open-shift
    // lookup (the dispatch board's truck badge).
    index('shifts_active_truck_idx').on(t.activeTruckId),
    index('shifts_active_trailer_idx').on(t.activeTrailerId),
    check(
      'shifts_ended_reason_check',
      sql`ended_reason IS NULL OR ended_reason IN ('DRIVER_END_DAY','NEW_SHIFT_START','IDLE_AUTO_CLOSE','MAX_DURATION')`,
    ),
    check(
      'shifts_opened_via_check',
      sql`opened_via IN ('ONLINE_START','OFFLINE_START','AUTO_SYNC')`,
    ),
    pgPolicy('shifts_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type Shift = typeof shifts.$inferSelect;
export type NewShift = typeof shifts.$inferInsert;
export const shiftSelectSchema = createSelectSchema(shifts);
export const shiftInsertSchema = createInsertSchema(shifts);

/**
 * shift_id_aliases (migration 0190) — a phone-minted shift id the server folded into another
 * shift: ADOPTED (an AUTO_SYNC shift took the queued start), MERGED (the driver already had an
 * explicit open shift), OVERLAP (the start fell inside a closed shift). The phone's later
 * End Day and equipment changes name its own id, and resolve through here.
 *
 * Append-only for app_user (SELECT, INSERT). RLS-enforced.
 */
export const shiftIdAliases = pgTable(
  'shift_id_aliases',
  {
    aliasId: uuid('alias_id').primaryKey(),
    shiftId: uuid('shift_id')
      .notNull()
      .references(() => shifts.id, { onDelete: 'cascade' }),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    driverId: uuid('driver_id')
      .notNull()
      .references(() => users.id, { onDelete: 'restrict' }),
    reason: text('reason').notNull(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index('shift_id_aliases_shift_idx').on(t.shiftId),
    check('shift_id_aliases_reason_check', sql`reason IN ('ADOPTED','MERGED','OVERLAP')`),
    pgPolicy('shift_id_aliases_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type ShiftIdAlias = typeof shiftIdAliases.$inferSelect;

/**
 * TIMECLOCK_EVENT_TYPES — exactly 4 values pinned verbatim from master spec
 * §11 (CLOCK AND BILLING — EVENT CHAIN MODEL, docs/CLAUDE.md line 571):
 *
 *     event_type ENUM('STARTED','PAUSED','RESUMED','STOPPED') NOT NULL
 *
 * NO additional event types. Shift start/end events live on the `shifts`
 * table (started_at / ended_at columns); this table is for per-job clock
 * transitions only. The pairing rule (PAUSED → RESUMED chronologically) is
 * the basis for Phase 12 billable_minutes derivation (master spec §11
 * lines 581-590).
 */
export const TIMECLOCK_EVENT_TYPES = ['STARTED', 'PAUSED', 'RESUMED', 'STOPPED'] as const;
export type TimeclockEventType = (typeof TIMECLOCK_EVENT_TYPES)[number];

/**
 * timeclock_events — append-only per DEC-001 + master spec §11.
 *
 * RLS-enforced. T-02-02-02 HIGH (billing depends on chain integrity) — Phase
 * 5+ application code MUST NOT UPDATE or DELETE rows; only INSERT. A Phase 23
 * defense-in-depth ALTER may add a Postgres trigger raising on UPDATE/DELETE
 * for non-superuser.
 *
 * Three indexes (RESEARCH #14, #15, #16):
 *   - (tenant_id, job_id, occurred_at) — event-chain reconstruction for
 *     billable-time calc (Phase 12 — CRITICAL).
 *   - (tenant_id, driver_id, occurred_at DESC) — driver timeline view
 *     (TICKET-05 + REVIEW-01).
 *   - (previous_event_id) — tamper-evident chain validation.
 *
 * `previous_event_id` is a self-FK (chain head has NULL). Phase 5+ writes
 * always set this to the most recent event id in the same shift_id chain.
 */
export const timeclockEvents = pgTable(
  'timeclock_events',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    driverId: uuid('driver_id')
      .notNull()
      .references(() => users.id, { onDelete: 'restrict' }),
    shiftId: uuid('shift_id')
      .notNull()
      .references(() => shifts.id, { onDelete: 'cascade' }),
    // Nullable — start/end-shift events have no job; per-job transitions do.
    jobId: uuid('job_id').references(() => jobs.id, { onDelete: 'restrict' }),
    // CHECK constraint defined below in the (t) => [...] callback.
    eventType: text('event_type').notNull(),
    // When the event happened on the device (driver clock).
    occurredAt: timestamp('occurred_at', { withTimezone: true }).notNull(),
    // When the server received the event (server clock — for sync-ordering analysis).
    syncedAt: timestamp('synced_at', { withTimezone: true }).notNull().defaultNow(),
    // Self-FK for chain validation; nullable for chain-head event.
    previousEventId: uuid('previous_event_id'),
    gpsLat: numeric('gps_lat', { precision: 10, scale: 7 }),
    gpsLng: numeric('gps_lng', { precision: 10, scale: 7 }),
    gpsAccuracyFt: integer('gps_accuracy_ft'),
    // TRUE = device was online when event happened (sync wave 1); FALSE = offline.
    onlineFlag: boolean('online_flag'),
    deviceId: uuid('device_id'),
    // Phase 6 / Plan 06-01 / D-22 step 7 / D-10 — categorises WHO/WHAT
    // initiated the event. CHECK constraint below restricts to 5 values:
    //   DRIVER                     — normal driver-triggered STARTED/PAUSED/RESUMED/STOPPED
    //   AUTO_PAUSE_FOR_JOB_SWITCH  — server auto-pauses prior chain when driver
    //                                resumes a different paused job (D-09)
    //   DISPATCHER_CLOSED          — dispatcher force-closed via /v1/jobs/:id/dispatcher-close (D-13)
    //   DISPATCHER_RESUMED         — DISPATCH_MANAGER+ resumed a driver's paused chain via
    //                                /v1/jobs/:id/dispatcher-resume (DEC-273, migration 0102).
    //                                A dispatcher-caused RESUMED must NOT be stamped 'DRIVER':
    //                                billing replays read this column, and a row claiming the
    //                                driver tapped resume is the Bug-A class of chain lie.
    //   SYSTEM                     — system-emitted (e.g. shift force-end)
    // Default 'DRIVER' so legacy rows + omitted fields backfill safely.
    triggeredBy: text('triggered_by').notNull().default('DRIVER'),
    // Phase 6 / Plan 06-01 / D-22 step 8 / D-13 — required (length >= 8) only
    // when triggered_by = 'DISPATCHER_CLOSED'. Conditional CHECK enforced
    // below in the (t) => [...] callback.
    reason: text('reason'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    // RESEARCH perf index #14 — billable-time calc per job (CRITICAL).
    index('timeclock_events_tenant_job_occurred_idx').on(t.tenantId, t.jobId, t.occurredAt),
    // RESEARCH perf index #15 — driver timeline (newest first).
    index('timeclock_events_tenant_driver_occurred_idx').on(
      t.tenantId,
      t.driverId,
      t.occurredAt.desc(),
    ),
    // RESEARCH perf index #16 — chain validation lookup.
    index('timeclock_events_previous_idx').on(t.previousEventId),
    // Master spec §11 line 571 — exactly 4 event types.
    check(
      'timeclock_events_type_check',
      sql`event_type IN ('STARTED', 'PAUSED', 'RESUMED', 'STOPPED')`,
    ),
    // Phase 6 / Plan 06-01 / D-22 step 7 — triggered_by allowlist.
    // DISPATCHER_RESUMED added by DEC-273 / migration 0102.
    check(
      'timeclock_events_triggered_by_check',
      sql`triggered_by IN ('DRIVER', 'AUTO_PAUSE_FOR_JOB_SWITCH', 'DISPATCHER_CLOSED', 'DISPATCHER_RESUMED', 'SYSTEM')`,
    ),
    // Phase 6 / Plan 06-01 / D-22 step 8 / D-13 — the two ELEVATED actions on a
    // driver's chain require a stated reason of >= 8 characters after trimming.
    // All other triggered_by values may have NULL reason.
    //   DISPATCHER_CLOSED  — force-close, since 0005.
    //   DISPATCHER_RESUMED — resume-on-behalf, added by DEC-275 / 0104.
    // btrim added by DEC-277 / 0106: `length(reason)` counted eight spaces as a
    // reason on BOTH routes. Keep this in step with that migration — it is the
    // declaration drizzle-kit diffs against, and 0104 shipped without updating
    // it, which is the drift this line also repairs.
    check(
      'timeclock_events_reason_required_check',
      sql`triggered_by NOT IN ('DISPATCHER_CLOSED', 'DISPATCHER_RESUMED') OR (reason IS NOT NULL AND length(btrim(reason)) >= 8)`,
    ),
    pgPolicy('timeclock_events_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

/**
 * TIMECLOCK_TRIGGERED_BY_VALUES — exactly 4 values pinned by D-22 step 7 / D-10.
 *
 * Stored as `text` with a CHECK constraint at the column level (not pgEnum)
 * matching the broader §11 pattern. The route layer (Phase 6 /v1/jobs/:id/resume
 * + /v1/jobs/:id/dispatcher-close) enforces that drivers can only submit
 * 'DRIVER'; the server injects 'AUTO_PAUSE_FOR_JOB_SWITCH' and 'DISPATCHER_CLOSED'
 * from within the elevated-permission routes.
 */
export const TIMECLOCK_TRIGGERED_BY_VALUES = [
  'DRIVER',
  'AUTO_PAUSE_FOR_JOB_SWITCH',
  'DISPATCHER_CLOSED',
  'SYSTEM',
] as const;
export type TimeclockTriggeredBy = (typeof TIMECLOCK_TRIGGERED_BY_VALUES)[number];

export type TimeclockEvent = typeof timeclockEvents.$inferSelect;
export type NewTimeclockEvent = typeof timeclockEvents.$inferInsert;
export const timeclockEventSelectSchema = createSelectSchema(timeclockEvents);
export const timeclockEventInsertSchema = createInsertSchema(timeclockEvents);
